import { yupResolver } from "@hookform/resolvers/yup";
import { iUser } from "core/kangarooTypes/iUser";
import { iMerchantFee } from "core/kangarooTypes/MerchantFee";
import {
  addMerchantFees,
  deleteMerchantFee,
  getMerchantFees,
  getUsers,
  updateMerchantFees,
} from "core/Services/AdminServices";
import {
  ChevronDown,
  Loader,
  Pencil,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { toast } from "react-toastify";
import Button from "shared/Button";
import { Dialog } from "shared/Dialog/Dialog";
import { DialogContent } from "shared/Dialog/DialogContent";
import { DialogFooter } from "shared/Dialog/DialogFooter";
import { DialogHeader } from "shared/Dialog/DialogHeader";
import { DialogTitle } from "shared/Dialog/DialogTitle";
import FormInput from "shared/FormInput";
import * as yup from "yup";
import { useTranslation } from "react-i18next";

const MerchantFeeManagement = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [merchantFees, setMerchantFees] = useState<iMerchantFee[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingMerchantFee, setEditingMerchantFee] =
  useState<iMerchantFee | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [users, setUsers] = useState<iUser[]>([]);
  const [deletingFeeId, setDeletingFeeId] = useState<number | null>(null);
  
  const MerchantFeeSchema = yup.object({
    value: yup
      .number()
      .required(t("merchantFeeManagement.validation.discountRequired"))
      .min(0, t("merchantFeeManagement.validation.discountPositive"))
      .max(100, t("merchantFeeManagement.validation.discountMax")),
    merchantId: yup.number().required(t("merchantFeeManagement.validation.merchantRequired")),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<MerchantFeeFormData>({
    resolver: yupResolver(MerchantFeeSchema),
  });

  const fetchAllData = async () => {
    try {
      setIsLoading(true);
      const [usersResponse, merchantFeesResponse] = await Promise.all([
        getUsers({ role: "merchant" }),
        getMerchantFees(),
      ]);
      setUsers(usersResponse.data);
      setMerchantFees(merchantFeesResponse.data);
    } catch (error) {
      toast.error(t("merchantFeeManagement.messages.saveError"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const handleAddMerchantFee = () => {
    setEditingMerchantFee(null);
    setShowModal(true);
  };

  const handleEditMerchantFee = (merchantFee: iMerchantFee) => {
    setEditingMerchantFee(merchantFee);
    setShowModal(true);
    setValue("value", merchantFee.value);
    setValue("merchantId", merchantFee.merchant.id);
  };

  const handleDeleteMerchantFee = async (id: number) => {
    try {
      setDeletingFeeId(id);
      await deleteMerchantFee(id);
      setMerchantFees(merchantFees.filter((fee) => fee.id !== id));
      toast.success(t("merchantFeeManagement.messages.deleteSuccess"));
    } catch (error) {
      toast.error(t("merchantFeeManagement.messages.deleteError"));
    } finally {
      setDeletingFeeId(null);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    reset();
    setEditingMerchantFee(null);
  };

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const onSubmit = async (data: MerchantFeeFormData) => {
    try {
      setIsSubmitting(true);
      if (editingMerchantFee) {
        await updateMerchantFees(data, editingMerchantFee.id);
        toast.success(t("merchantFeeManagement.messages.updateSuccess"));
      } else {
        await addMerchantFees(data);
        toast.success(t("merchantFeeManagement.messages.addSuccess"));
      }
      await fetchAllData();
      handleCloseModal();
    } catch (error) {
      toast.error(t("merchantFeeManagement.messages.saveError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredMerchantFees = merchantFees.filter(
    (fee) =>
      fee.merchant.business_name &&
      fee.merchant.business_name
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  const MerchantFeeTableSkeleton = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-4 md:px-6 py-4 text-left text-sm font-medium text-gray-500">
              {t("merchantFeeManagement.merchantName")}
            </th>
            <th className="px-4 md:px-6 py-4 text-left text-sm font-medium text-gray-500">
              {t("merchantFeeManagement.discountPercentage")}
            </th>
            <th className="px-4 md:px-6 py-4 text-right text-sm font-medium text-gray-500">
              {t("merchantFeeManagement.actions")}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {[...Array(5)].map((_, index) => (
            <tr key={index} className="hover:bg-gray-50">
              <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                <Skeleton width={120} />
              </td>
              <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                <Skeleton width={80} />
              </td>
              <td className="px-4 md:px-6 py-4 whitespace-nowrap text-right">
                <div className="flex justify-end gap-2">
                  <Skeleton width={20} height={20} circle />
                  <Skeleton width={20} height={20} circle />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="bg-white p-4 md:p-6 rounded-lg shadow-md">
      {/* Mobile Filter Toggle */}
      <div className="md:hidden flex justify-between items-center mb-4">
        <h2 className="text-xl font-medium text-gray-900">
          {t("merchantFeeManagement.title")}
        </h2>
      </div>

      {/* Search and Add Button Area */}
      <div className="pb-4">
        <h2 className="hidden md:block text-xl font-medium text-gray-900">
          {t("merchantFeeManagement.title")}
        </h2>
        <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
          <div className="relative w-full md:w-64">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder={t("merchantFeeManagement.searchPlaceholder")}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button
            onClick={handleAddMerchantFee}
            icon={<Plus size={16} />}
            className="w-full md:w-auto"
          >
            {t("merchantFeeManagement.addMerchantFee")}
          </Button>
        </div>
      </div>

      {/* Table Content */}
      {isLoading ? (
        <MerchantFeeTableSkeleton />
      ) : filteredMerchantFees.length === 0 ? (
        <div className="p-4 border border-yellow-300 bg-yellow-50 rounded-md text-yellow-800 text-center">
          {t("merchantFeeManagement.noFeesFound")}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 md:px-6 py-4 text-left text-sm font-medium text-gray-500">
                  {t("merchantFeeManagement.merchantName")}
                </th>
                <th className="px-4 md:px-6 py-4 text-left text-sm font-medium text-gray-500">
                  {t("merchantFeeManagement.discountPercentage")}
                </th>
                <th className="px-4 md:px-6 py-4 text-right text-sm font-medium text-gray-500">
                  {t("merchantFeeManagement.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredMerchantFees.map((fee) => (
                <tr key={fee.id} className="hover:bg-gray-50">
                  <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {fee.merchant.business_name}
                  </td>
                  <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {fee.value}%
                  </td>
                  <td className="px-4 md:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        className="text-blue-600 hover:text-blue-900"
                        onClick={() => handleEditMerchantFee(fee)}
                      >
                        <Pencil className="h-5 w-5" />
                      </button>
                      <button
                        className={`text-red-600 hover:text-red-900 ${
                          deletingFeeId === fee.id
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                        onClick={() => handleDeleteMerchantFee(fee.id)}
                        disabled={deletingFeeId === fee.id}
                      >
                        {deletingFeeId === fee.id ? (
                          <Loader className="h-5 w-5 animate-spin" />
                        ) : (
                          <Trash2 className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={handleCloseModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex justify-between items-center">
              <span>
                {editingMerchantFee
                  ? t("merchantFeeManagement.editMerchantFee")
                  : t("merchantFeeManagement.addNewMerchantFee")}
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="p-4 sm:p-6 flex flex-col h-full justify-start">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormInput
                label={t("merchantFeeManagement.merchant")}
                name="merchantId"
                type="select"
                register={register}
                errors={errors}
                selectOptions={users.map((merchant) => ({
                  value: merchant.id,
                  label: merchant.business_name,
                }))}
                disabled={!!editingMerchantFee || isLoading}
                icon={<ChevronDown size={20} />}
              />
              <FormInput
                label={t("merchantFeeManagement.discountPercentageLabel")}
                name="value"
                type="number"
                register={register}
                errors={errors}
                placeholder={t("merchantFeeManagement.enterDiscountPercentage")}
              />
            </div>
          </div>

          <DialogFooter className="mt-4 sm:mt-6 flex flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outlined"
              onClick={handleCloseModal}
              className="w-full sm:w-auto"
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              isLoading={isSubmitting}
              className="w-full sm:w-auto"
            >
              {editingMerchantFee 
                ? t("merchantFeeManagement.updateMerchantFee")
                : t("merchantFeeManagement.addMerchantFee")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MerchantFeeManagement;
export type MerchantFeeFormData = {
  value: number;
  merchantId: number;
};
